/**
 * Popover positioning utilities using Floating UI
 *
 * Provides viewport-aware positioning with flip, shift, and size middleware
 * to ensure popovers never overflow the viewport.
 */

import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
  size,
  type Placement,
  type Middleware,
} from "@floating-ui/dom";

export type PopoverSide = "top" | "bottom" | "left" | "right";
export type PopoverAlign = "start" | "center" | "end";

export type PositionConfig = {
  side: PopoverSide;
  align: PopoverAlign;
  viewportPadding: number;
  /** Offset from the trigger element in pixels */
  offsetDistance?: number;
};

/**
 * Convert side + align to Floating UI placement
 */
export const toFloatingPlacement = (side: PopoverSide, align: PopoverAlign): Placement => {
  if (align === "center") return side;
  return `${side}-${align}` as Placement;
};

/**
 * Build middleware array for Floating UI
 */
export const buildMiddleware = (config: PositionConfig): Middleware[] => {
  const { viewportPadding, offsetDistance = 8 } = config;

  return [
    // Offset from trigger
    offset(offsetDistance),

    // Flip ONLY across the trigger on the REQUESTED axis. A panel asked to open
    // below its trigger falls back to `top` (same alignment) and nothing else; a
    // `left`/`right` panel falls back only to the opposite horizontal side. The
    // default `fallbackPlacements` derive the opposite placement of the same
    // axis, so we do not list them; `fallbackAxisSideDirection: "none"` forbids
    // the perpendicular axis outright (a `bottom` panel can never land on the
    // left or right); `crossAxis: false` means only main-axis overflow (not
    // enough room on the requested side) triggers a flip -- edge crowding on the
    // other axis is `shift`'s job, not a reason to jump sides.
    flip({
      padding: viewportPadding,
      crossAxis: false,
      fallbackAxisSideDirection: "none",
    }),

    // Shift along the MAIN alignment axis only. For a top/bottom panel that is
    // horizontal sliding to stay within the left/right viewport edges; for a
    // left/right panel it is vertical sliding. `crossAxis: false` keeps the
    // perpendicular axis fixed, so the panel stays glued to its trigger's edge
    // and can never detach to slide up over the page header (the reported bug).
    shift({
      padding: viewportPadding,
      crossAxis: false,
    }),

    // Constrain size to available space.
    //
    // This publishes the available space as `--qb-available-w/h` custom
    // properties (and the trigger width as `--qb-trigger-w`) rather than writing
    // `max-width`/`max-height` inline. An inline dimension outranks a class in
    // the cascade, so writing them here silently killed every consumer's own cap
    // -- e.g. Select's `max-h-[min(70vh,320px)]` rendered a ~900px list near the
    // top of a tall viewport. Consumers now compose their design cap against
    // these vars (`max-h-[min(320px,var(--qb-available-h,100vh))]`), keeping
    // BOTH their cap and this viewport clamp. `--qb-trigger-w` lets a select-type
    // panel hold a `min-width` of its trigger so its list never renders narrower
    // than the control it belongs to. `size()` still runs -- flip and shift
    // depend on it reserving space -- only its `apply` stops mutating layout
    // dimensions.
    //
    // The values are the true available space (floored at 0), never an inflated
    // floor: an old `Math.max(150, ...)` could publish more room than actually
    // existed and guarantee the very overflow this middleware prevents.
    size({
      padding: viewportPadding,
      apply({ availableWidth, availableHeight, elements, rects }) {
        const availW = Math.max(0, Math.floor(availableWidth));
        const availH = Math.max(0, Math.floor(availableHeight));
        const triggerW = Math.max(0, Math.round(rects.reference.width));
        elements.floating.style.setProperty("--qb-available-w", `${availW}px`);
        elements.floating.style.setProperty("--qb-available-h", `${availH}px`);
        elements.floating.style.setProperty("--qb-trigger-w", `${triggerW}px`);
      },
    }),
  ];
};

export type PositionResult = {
  x: number;
  y: number;
  placement: Placement;
  maxHeight?: number;
};

/**
 * Compute popover position using Floating UI
 */
export const computePopoverPosition = async (
  trigger: HTMLElement,
  panel: HTMLElement,
  config: PositionConfig
): Promise<PositionResult> => {
  const placement = toFloatingPlacement(config.side, config.align);
  const middleware = buildMiddleware(config);

  const result = await computePosition(trigger, panel, {
    placement,
    middleware,
    strategy: "fixed",
  });

  return {
    x: result.x,
    y: result.y,
    placement: result.placement,
  };
};

/**
 * Set up auto-update for popover position (scroll, resize, etc.)
 * Returns cleanup function.
 */
export const setupAutoUpdate = (
  trigger: HTMLElement,
  panel: HTMLElement,
  config: PositionConfig,
  onUpdate: (result: PositionResult) => void
): (() => void) => {
  const update = async () => {
    const result = await computePopoverPosition(trigger, panel, config);
    onUpdate(result);
  };

  // `autoUpdate` runs `update` itself as it starts, so the initial position is
  // already covered. Calling it here as well left two computations racing for
  // the same first paint, and whichever settled last won.
  return autoUpdate(trigger, panel, update, {
    ancestorScroll: true,
    ancestorResize: true,
    elementResize: true,
    layoutShift: true,
  });
};
